import { isNotNull } from "./null-or-empty";

export function arrayEquals<T, K = T>(a: T[] | undefined | null, b: T[] | undefined | null, byFn?: (v: T) => K): boolean {
  if (a == null || b == null) return a === b;
  if (a === b) return true;
  if (a.length !== b.length) return false;
  const bKeys = new Set<T | K>(byFn ? b.map(byFn) : b);
  return a.length === b.length && a.every((v) => bKeys.has(byFn ? byFn(v) : v));
}

export function arrayEqualsInOrder<T, K = T>(a: T[], b: T[], byFn?: (v: T) => K): boolean {
  return a === b || (a.length === b.length && a.every((v, i) => (byFn ? byFn(v) === byFn(b[i]) : v === b[i])));
}

export function flatMap<T, R = T>(arr: T[], fn: (value: T) => R | R[]): R[] {
  return flatten(arr.map(fn));
}

export function flatten<T>(arr: Array<T | T[]>): T[] {
  return ([] as T[]).concat(...arr);
}

export function pick<T extends Record<string, any>, K extends keyof T>(obj: T, keys: K[]) {
  const result: Partial<T> = {};
  for (const key of keys) result[key] = obj[key];
  return result as { [key in (typeof keys)[number]]: T[key] };
}

export function _min<T extends number>(args: Array<T | undefined | null>): T | undefined;
export function _min<T, R extends number>(args: Array<T | undefined | null>, selector: (v: T) => R | undefined | null): R | undefined;
export function _min(args: any[], selector?: (v: any) => number): number | undefined {
  const items = (selector ? args.map(selector) : args).filter(isNotNull);
  if (!items.length) return;
  return Math.min(...items);
}

export function _max<T extends number>(args: Array<T | undefined | null>): T | undefined;
export function _max<T, R extends number>(args: Array<T | undefined | null>, selector: (v: T) => R | undefined | null): R | undefined;
export function _max(args: any[], selector?: (v: any) => number): number | undefined {
  const items = (selector ? args.map(selector) : args).filter(isNotNull);
  if (!items.length) return;
  return Math.max(...items);
}

export function _same<T, R>(args: Array<T>, fn: (v: T) => R): R | undefined {
  const rs = args.map(fn);
  const first = rs[0];
  return rs.every((r) => r === first) ? first : undefined;
}

export namespace _Array {
  export function ifEmpty<T>(arr: T[], otherwise: T[]) {
    return !arr.length ? otherwise : arr;
  }

  export function filterBy<T, K extends keyof T>(arr: T[], key: K, keyValues: T[K][]) {
    return arr.filter((o) => keyValues.includes(o[key]));
  }

  export function groupBy<T, K, R = T>(arr: T[], getKey: (item: T) => K, project?: (item: T) => R): Map<K, R[]> {
    const result = new Map<K, R[]>();
    for (const item of arr) {
      const key = getKey(item);
      const existing = result.get(key);
      const projected = project ? project(item) : (item as unknown as R);
      if (existing) {
        existing.push(projected);
      } else {
        result.set(key, [projected]);
      }
    }
    return result;
  }
}

export namespace Group {
  //
  export function intersection<T>(a: T[], b: T[]): T[] {
    return a.filter((x) => b.includes(x));
  }

  export function subtract<T>(a: T[], b: T[]): T[] {
    return a.filter((x) => !b.includes(x));
  }

  export function subtractOne<T>(a: T[], b: T): T[] {
    return a.filter((x) => x !== b);
  }

  export function merge<T, K = T>(a: T[], b: T[], byFn?: (v: T) => K): T[] {
    if (byFn == null) return Array.from(new Set([...a, ...b]));
    const merged = new Map(a.map((v) => [byFn(v), v]));
    for (const bi of b) merged.set(byFn(bi), bi);
    return Array.from(merged.values());
  }

  export function hasAtLeastOneItem<T>(arr: T[] | undefined | null): arr is [T, ...T[]] {
    return (arr?.length ?? 0) > 0;
  }

  export function ifEmpty<T, B extends T[]>(a: T[] | undefined | null, b: B): [T, ...T[]] | B {
    return hasAtLeastOneItem(a) ? a : b;
  }

  export function toObject<T, K extends string>(arr: T[], byFn: (value: T) => K): { [key in K]?: T } {
    let result: { [key in K]?: T } = {};
    for (const item of arr) {
      result[byFn(item)] = item;
    }
    return result;
  }

  export function toMap<T, K>(arr: T[], byFn: (value: T) => K): Map<K, T[]> {
    const result = new Map<K, T[]>();
    for (const item of arr) {
      const key = byFn(item);
      let inMap = result.get(key);
      if (inMap == null) {
        inMap = [];
        result.set(key, inMap);
      }
      inMap.push(item);
    }
    return result;
  }

  export function unique<T, K>(arr: T[], byFn: (value: T) => K): T[] {
    return Array.from(new Map(arr.map((f) => [byFn(f), f])).values());
  }
}
